import Head from "expo-router/head";

import type { JsonLdValue, SeoHeadProps } from "./SeoHead.types";

function getJsonLdItems(jsonLd: SeoHeadProps["jsonLd"]): JsonLdValue[] {
  if (!jsonLd) return [];
  return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
}

function serializeJsonLd(item: JsonLdValue): string {
  return JSON.stringify(item).replace(/</g, "\\u003c");
}

export default function SeoHead({
  title,
  description,
  canonicalUrl,
  imageUrl,
  openGraphType = "website",
  noIndex = false,
  jsonLd,
}: SeoHeadProps) {
  const cleanDescription = description?.trim() || undefined;
  const cleanCanonicalUrl = canonicalUrl?.trim() || undefined;
  const cleanImageUrl = imageUrl?.trim() || undefined;
  const jsonLdItems = getJsonLdItems(jsonLd);

  return (
    <Head>
      <title>{title}</title>

      {cleanDescription ? (
        <meta name="description" content={cleanDescription} />
      ) : null}

      {noIndex ? <meta name="robots" content="noindex,nofollow" /> : null}

      {cleanCanonicalUrl ? (
        <link rel="canonical" href={cleanCanonicalUrl} />
      ) : null}

      <meta property="og:type" content={openGraphType} />
      <meta property="og:site_name" content="Soko Grid" />
      <meta property="og:title" content={title} />

      {cleanDescription ? (
        <meta property="og:description" content={cleanDescription} />
      ) : null}

      {cleanCanonicalUrl ? (
        <meta property="og:url" content={cleanCanonicalUrl} />
      ) : null}

      {cleanImageUrl ? <meta property="og:image" content={cleanImageUrl} /> : null}
      {cleanImageUrl ? (
        <meta property="og:image:alt" content="Soko Grid" />
      ) : null}

      <meta
        name="twitter:card"
        content={cleanImageUrl ? "summary_large_image" : "summary"}
      />
      <meta name="twitter:title" content={title} />

      {cleanDescription ? (
        <meta name="twitter:description" content={cleanDescription} />
      ) : null}

      {cleanImageUrl ? (
        <meta name="twitter:image" content={cleanImageUrl} />
      ) : null}

      {jsonLdItems.map((item, index) => (
        <script
          // JSON-LD order is stable because callers pass deterministic arrays.
          key={`json-ld-${index}`}
          type="application/ld+json"
        >
          {serializeJsonLd(item)}
        </script>
      ))}
    </Head>
  );
}
